import React, {Component} from "react"
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ZAxis,
  Line,
  Scatter,
  Cell,
  Tooltip as ChartTooltip
} from "recharts"

import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import {PaymentCurrencyType} from "@irati/types"
import theme from "@irati/ui/src/global/theme"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import Flex from "@irati/ui/src/Flex"
import {Label, H4} from "@irati/ui/src/Typography"

import {ChartWrapper, TooltipWrapper} from "./styled"
import {TrendTypes} from "./enums"

interface Row {
  value: number
  date: string
}

interface State {
  focusBarIndex: number | null
  mouseLeave: boolean
}

interface Props {
  selectedTrendRate: TrendTypes
  selectedTrendRateLabel: string
  currencyCode: PaymentCurrencyType
  data: Row[]
}

class BarChart extends Component<Props, State> {
  state: State = {
    focusBarIndex: null,
    mouseLeave: true
  }

  tickFormatter = (item: number) => {
    const {selectedTrendRate, currencyCode} = this.props
    if (selectedTrendRate === TrendTypes.engagementRate || selectedTrendRate === TrendTypes.savingsPercentage) {
      return `${Math.round(item)}%`
    }
    if (selectedTrendRate === TrendTypes.savings) {
      return formatCurrency({value: item, currencyCode, isCents: false})
    }
    return item
  }

  onHoverState = (state: any) => {
    if (state.isTooltipActive) {
      this.setState({focusBarIndex: state.activeTooltipIndex, mouseLeave: false})
    } else {
      this.setState({focusBarIndex: null, mouseLeave: true})
    }
  }

  render() {
    const {data, selectedTrendRateLabel} = this.props
    const {focusBarIndex, mouseLeave} = this.state

    return (
      <ChartWrapper index={focusBarIndex + 1} onMouseLeave={() => this.setState({focusBarIndex: null, mouseLeave: true})}>
        <ResponsiveContainer height={500}>
          <ComposedChart
            width={500}
            height={400}
            data={data}
            margin={{
              top: 0,
              right: 0,
              bottom: 0,
              left: 0
            }}
            onMouseMove={(state: any) => this.onHoverState(state)}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="date" axisLine={false} tickLine={false} />
            <YAxis tickCount={6} tickFormatter={this.tickFormatter} axisLine={false} tickLine={false} />
            <ZAxis dataKey="value" range={[30, 30]} />
            <ChartTooltip
              content={
                <TooltipWrapper padding={2} margin={2}>
                  <Label color={theme.colors.primary1}>
                    <FormattedMessage id={selectedTrendRateLabel} />
                  </Label>
                  <Flex marginTop={1} marginBottom={1} justifyContent="space-between" gap="16px">
                    <Label>{data[focusBarIndex ? focusBarIndex : 0]?.date}</Label>
                    <H4>{this.tickFormatter(data[focusBarIndex ? focusBarIndex : 0]?.value)}</H4>
                  </Flex>
                </TooltipWrapper>
              }
            />
            <Bar id="adminSection_trendRatesBarChart" dataKey="value" fill={theme.colors.primary2} barSize={40}>
              {data.map((_, index) => {
                return (
                  <Cell key={index} fill={focusBarIndex === index || mouseLeave ? theme.colors.primary2 : theme.colors.primary2_100} />
                )
              })}
            </Bar>
            <Line type="monotone" dataKey="value" stroke={theme.colors.primary1} strokeDasharray="4 4" dot={false} />
            {!mouseLeave && <Scatter dataKey="value" shape="circle" stroke={theme.colors.primary2} fill={theme.colors.primary1} />}
          </ComposedChart>
        </ResponsiveContainer>
      </ChartWrapper>
    )
  }
}

export default injectIntl(BarChart)
